import React, { useState, useEffect, useRef } from 'react';
import {
  Group,
  Stack,
  Text,
  Paper,
  SimpleGrid,
  useMantineTheme,
  Chip,
  Divider,
  Select
} from '@mantine/core';
import DateRangeFilter from 'components/DateRangeFilter';
import { IconCalendar, IconClock, IconMapPin } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import Card from 'components/Card';
import PrimaryButton from 'components/PrimaryButton';
import SecondaryButton from 'components/SecondaryButton';
import dayjs from 'dayjs';
import { masterAreaService } from 'services/dacum/masterAreaService';
import { useMediaQuery } from '@mantine/hooks';

export interface OverviewFilters {
  startDate?: Date | null;
  endDate?: Date | null;
  preset?: string | null;
  areaID?: string | null;
}

interface OverviewFilterProps {
  /* eslint-disable */
  filters: OverviewFilters;
  onFiltersChange: (filters: OverviewFilters) => void;
  onApply: (filters: OverviewFilters) => void;
  isLoading?: boolean;
  /* eslint-enable */
}

const PRESET_OPTIONS = [
  { label: 'Last 30 Days', value: '30', days: 30 },
  { label: 'Last 60 Days', value: '60', days: 60 },
  { label: 'Last 120 Days', value: '120', days: 120 },
  { label: 'Last 240 Days', value: '240', days: 240 },
  { label: 'Last Year', value: '365', days: 365 }
];

const params = {
  pageSize: 1000,
  pageNumber: 1
};

const OverviewFilter: React.FC<OverviewFilterProps> = ({
  filters,
  onFiltersChange,
  onApply,
  isLoading = false
}) => {
  const [localFilters, setLocalFilters] = useState<OverviewFilters>(filters);
  const latestFiltersRef = useRef<OverviewFilters>(filters);
  const theme = useMantineTheme();
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  type FiltersUpdater = OverviewFilters | ((_prev: OverviewFilters) => OverviewFilters);

  const updateLocalFilters = (updater: FiltersUpdater) => {
    setLocalFilters((prev) => {
      const next =
        typeof updater === 'function'
          ? (updater as (_prev: OverviewFilters) => OverviewFilters)(prev)
          : updater;
      latestFiltersRef.current = next;
      return next;
    });
  };

  // Fetch Area data (same as BCLDCustom creatorCode)
  const { data: dataMasterArea, isPending: loadingMasterArea } = useQuery({
    queryKey: ['masterAreaServiceGetList'],
    queryFn: () => masterAreaService.getList({ page: params.pageNumber, limit: params.pageSize }),
    staleTime: Infinity
  });

  useEffect(() => {
    updateLocalFilters(filters);
  }, [filters]);

  const handlePresetClick = (preset: (typeof PRESET_OPTIONS)[0]) => {
    const endDate = new Date();
    const startDate = dayjs().subtract(preset.days, 'day').toDate();

    const newFilters = {
      ...localFilters,
      startDate,
      endDate,
      preset: preset.value
    };

    updateLocalFilters(newFilters);
  };

  const handleAreaChange = (value: string | null) => {
    updateLocalFilters((prev) => ({
      ...prev,
      areaID: value === '' ? null : value
    }));
  };

  const handleClear = () => {
    const clearedFilters = {
      startDate: null,
      endDate: null,
      preset: null,
      areaID: null
    };

    updateLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    onApply(clearedFilters);
  };

  const hasActiveFilters =
    localFilters.startDate || localFilters.endDate || localFilters.preset || localFilters.areaID;

  const areaOptions = React.useMemo(() => {
    const options =
      dataMasterArea?.data?.map((item: any) => ({
        value: item.areaID,
        label: item.areaName
      })) || [];

    // Ensure the currently selected area is in the options
    if (localFilters.areaID && !options.some((opt) => opt.value === localFilters.areaID)) {
      const selectedArea = dataMasterArea?.data?.find(
        (item: any) => item.areaID === localFilters.areaID
      );
      if (selectedArea) {
        options.unshift({
          value: selectedArea.areaID,
          label: selectedArea.areaName
        });
      }
    }

    return options;
  }, [dataMasterArea?.data, localFilters.areaID]);

  const getSelectedAreaName = () => {
    if (!localFilters.areaID || !dataMasterArea?.data) return null;

    const areaData = dataMasterArea.data as any[];

    const selectedArea = areaData.find((area: any) => area.areaID === localFilters.areaID);
    return selectedArea?.areaName || null;
  };

  return (
    <Card>
      <Stack gap='md'>
        {/* Filter Content */}
        <SimpleGrid
          cols={{ base: 1, xs: 2, md: 3 }}
          spacing={{ base: 'md', sm: 'lg' }}
          verticalSpacing={{ base: 'md', sm: 'lg' }}
        >
          {/* Quick Presets */}
          <Stack gap='xs'>
            <Group gap='xs' align='center'>
              <IconClock size={16} />
              <Text size='sm' fw={500} c='dimmed'>
                Quick Select
              </Text>
            </Group>
            <Chip.Group
              value={localFilters.preset || ''}
              onChange={(value) => {
                if (value) {
                  const preset = PRESET_OPTIONS.find((p) => p.value === value);
                  if (preset) handlePresetClick(preset);
                } else {
                  updateLocalFilters((prev) => ({ ...prev, preset: null }));
                }
              }}
              multiple={false}
            >
              <Group gap='xs' wrap='wrap'>
                {PRESET_OPTIONS.map((preset) => (
                  <Chip
                    key={preset.value}
                    value={preset.value}
                    variant='light'
                    size='sm'
                    radius='md'
                    style={{ flex: isSmallScreen ? '1 1 auto' : 'none' }}
                  >
                    {preset.label}
                  </Chip>
                ))}
              </Group>
            </Chip.Group>
          </Stack>

          {/* Custom Date Range */}
          <Stack gap='xs'>
            <Group gap='xs' align='center'>
              <IconCalendar size={16} />
              <Text size='sm' fw={500} c='dimmed'>
                Custom Range
              </Text>
            </Group>
            <Stack gap='xs'>
              <Text size='xs' c='dimmed'>
                Select start and end dates
              </Text>
              <DateRangeFilter
                value={
                  localFilters.startDate && localFilters.endDate
                    ? [localFilters.startDate, localFilters.endDate]
                    : undefined
                }
                onChange={(value) => {
                  updateLocalFilters((prev) => ({
                    ...prev,
                    startDate: value?.[0] || null,
                    endDate: value?.[1] || null,
                    preset: null
                  }));
                }}
              />
            </Stack>
          </Stack>

          {/* Area Filter */}
          <Stack gap='xs'>
            <Group gap='xs' align='center'>
              <IconMapPin size={16} />
              <Text size='sm' fw={500} c='dimmed'>
                Filter by Area
              </Text>
            </Group>
            <Select
              placeholder='Select area'
              value={localFilters.areaID || ''}
              onChange={(value: string | null) => handleAreaChange(value)}
              data={areaOptions}
              disabled={loadingMasterArea}
              clearable
              size='sm'
              searchable
              w='100%'
            />
          </Stack>
        </SimpleGrid>

        <Divider my='md' />

        {/* Single Apply button for the whole filter */}
        <Stack align={isSmallScreen ? 'stretch' : 'flex-end'} mt='sm'>
          <PrimaryButton
            text='Apply'
            onClick={() => {
              onFiltersChange(latestFiltersRef.current);
              onApply(latestFiltersRef.current);
            }}
            loading={isLoading}
            disabled={JSON.stringify(localFilters) === JSON.stringify(filters)}
          />
        </Stack>

        {/* Active Filter Summary */}
        {hasActiveFilters && (
          <Paper p='sm' bg='blue.0' radius='sm'>
            <Stack gap='xs'>
              <Text size='sm' fw={500} c='blue.7'>
                Active Filters
              </Text>
              <Group gap='xs' wrap='wrap'>
                {localFilters.preset && (
                  <Chip
                    size='sm'
                    variant='filled'
                    color='blue'
                    onClick={() => updateLocalFilters((prev) => ({ ...prev, preset: null }))}
                    style={{ cursor: 'pointer' }}
                  >
                    {PRESET_OPTIONS.find((p) => p.value === localFilters.preset)?.label} ×
                  </Chip>
                )}
                {localFilters.startDate && localFilters.endDate && (
                  <Chip
                    size='sm'
                    variant='filled'
                    color='blue'
                    onClick={() =>
                      updateLocalFilters((prev) => ({ ...prev, startDate: null, endDate: null }))
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    {dayjs(localFilters.startDate).format('MMM DD, YYYY')} -{' '}
                    {dayjs(localFilters.endDate).format('MMM DD, YYYY')} ×
                  </Chip>
                )}
                {getSelectedAreaName() && (
                  <Chip
                    size='sm'
                    variant='filled'
                    color='blue'
                    onClick={() => updateLocalFilters((prev) => ({ ...prev, areaID: null }))}
                    style={{ cursor: 'pointer' }}
                  >
                    Area: {getSelectedAreaName()} ×
                  </Chip>
                )}
                <SecondaryButton text='Clear All' onClick={handleClear} />
              </Group>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Card>
  );
};

export default OverviewFilter;
